/** @jsxImportSource @emotion/react */

import { FC, MouseEvent, ReactNode, useState } from "react";
import { css } from "@emotion/react";
import "@material/drawer/dist/mdc.drawer.css";
import { Drawer, DrawerContent } from "@rmwc/drawer";
import MenuItem from "../../DataGrid/List/MenuItem";
import useConfigs from "../../ConfigContext/use-configs";
import useTheme from "../../../commons/hooks/use-theme";
import SidebarHeader from "./SidebarHeader";
import { CollapsibleList } from "@rmwc/list";
import chevronLeft from "@iconify/icons-mdi/chevron-left";
import Svg from "../../Image/Svg";
import Direction from "../../../commons/Direction";
import { RouteType } from "../../Navigation/Routes/CreateRoutes";

export type SideBarProps = {
  items?: RouteType[];
  open?: boolean;
  onClose?: () => void;
  headerTitle?: string;
  headerSubtitle?: string;
  headerIcon?: ReactNode;
  currentRoutePath?: string;
  modal?: boolean;
  dir?: Direction;
};

const Sidebar: FC<SideBarProps> = (props) => {
  let configs = useConfigs();
  const {
    headerIcon,
    headerSubtitle,
    headerTitle,
    currentRoutePath,
    open = false,
    onClose,
    items = [],
    dir = configs.direction,
  } = props;
  let theme = useTheme();

  const dismissible = window.innerWidth >= 768;
  const [mobileOpen, setMobileOpen] = useState(false);

  const styles = css`
    background: ${theme.sideBarBackground};
    box-shadow: 3px 0 5px 0 #36393f4d;
    .mdc-list-item {
      color: ${theme.material.onPrimary} !important;
    }
    .mdc-list-item--selected {
      color: ${theme.material.primary};
    }
  `;

  const [currentPaths, setCurrentPaths] = useState([]);

  const getPath = (routePath: string = "", parentPath?: string) => {
    return (parentPath !== undefined ? `${parentPath}_#_` : "") + routePath;
  };

  const handleClickCollapsibleList = (event: MouseEvent, path: string, parentPath?: string) => {
    event.preventDefault();
    event.stopPropagation();
    let result: string[];
    if (parentPath && parentPath.includes("_#_")) {
      result = [path, ...parentPath.split("_#_")];
    } else {
      result = [path, parentPath];
    }
    setCurrentPaths(result);
  };

  const renderMenuItem = (route?: any, level?: number, parentPath?: string) => {
    if (route.children && route.children.length) {
      return (
        <CollapsibleList
          key={route.path || route.title}
          onClick={(event) => handleClickCollapsibleList(event, route.path, parentPath)}
          open={currentPaths.includes(route.path)}
          dir={dir}
          handle={
            <MenuItem
              className={route.className}
              currentSelected={currentRoutePath}
              onClick={() => {
                setMobileOpen(true);
                route.onClick && route.onClick();
                !dismissible && onClose();
              }}
              icon={
                route.iconTag ? (
                  route.iconTag
                ) : (
                  <Svg icon={route.icon} src={route.iconSrc} color={theme.material.onPrimary} />
                )
              }
              text={route.title}
              metaIcon={<Svg icon={chevronLeft} color={theme.material.onPrimary} />}
              level={level}
              dir={dir}
            />
          }
        >
          {renderItems(route.children, level + 1, getPath(route.path, parentPath))}
        </CollapsibleList>
      );
    } else {
      return (
        <MenuItem
          key={route.path || route.title}
          currentSelected={currentRoutePath}
          className={route.className}
          path={route.path}
          onClick={() => {
            if (parentPath === undefined) {
              setCurrentPaths([]);
            }
            setMobileOpen(false);
            route.onClick && route.onClick();
            !dismissible && onClose();
          }}
          icon={
            route.iconTag ? (
              route.iconTag
            ) : (
              <Svg icon={route.icon} src={route.iconSrc} color={theme.material.onPrimary} />
            )
          }
          text={route.title}
          level={level}
          dir={dir}
        />
      );
    }
  };

  const renderItems = (routes: any, level: number = 0, parentPath?: string) => {
    if (routes.length) {
      return routes.map((route: any) => {
        if (route.onlyInMobile) {
          if (window.innerWidth < 768) return renderMenuItem(route, level, parentPath);
          else return null;
        } else return renderMenuItem(route, level, parentPath);
      });
    } else {
      return null;
    }
  };

  const handleOpen = () => {
    if (window.innerWidth < 768 && mobileOpen) return true;
    return open;
  };

  return (
    <Drawer
      css={styles}
      dir={dir}
      modal={!dismissible}
      dismissible={dismissible}
      open={handleOpen()}
      onClose={() => onClose()}
    >
      <SidebarHeader subtitle={headerSubtitle} icon={headerIcon}>
        {headerTitle}
      </SidebarHeader>
      <DrawerContent dir={dir}>{renderItems(items)}</DrawerContent>
    </Drawer>
  );
};
export default Sidebar;
